<template>
  <div>
    <CreateMatcherTaskV2
      ref="v2TaskCreationDialog"
      v-if="$store.getters.isMatcherApiV2Enabled"
    />
    <TaskCreationDialog ref="creationDialog" v-else />
    <v-row dense class="header pb-1">
      <v-col xs="12" sm="12" md="12" lg="12">
        <v-card>
          <v-card-title>
            <v-toolbar flat height="80" color="white">
              <v-toolbar-title>Tasks</v-toolbar-title>
              <v-divider class="mx-4" inset vertical />
              <v-tabs style="width: auto" @change="tabChange">
                <v-tab>Active</v-tab>
                <v-tab>Inactive</v-tab>
                <v-tab>All</v-tab>
              </v-tabs>
              <v-col class="pa-0" cols="auto">
                <v-row dense align="center">
                  <v-select
                    hide-details
                    return-object
                    class="search-context-select"
                    :items="searchContexts"
                    v-model="searchContext"
                    menu-props="auto"
                    label="Search By:"
                  />
                  <v-combobox
                    clearable
                    :items="searchSuggestions"
                    class="search-field"
                    height="auto"
                    hide-details="auto"
                    label="Search"
                    @change="handleSearchChange"
                    @keyup="({ target }) => handleSearchChange(target.value)"
                  >
                    <v-icon slot="append" color="primary">mdi-magnify</v-icon>
                  </v-combobox>
                  <v-btn dark color="primary" @click="openTaskCreationDialog">
                    New task
                  </v-btn>
                </v-row>
                <v-row dense class="pt-5">
                  <v-btn
                    outlined
                    class="mr-4"
                    color="primary"
                    @click="handleOpenTagsDialog"
                  >
                    <v-icon left>mdi-label</v-icon>
                    Tags
                  </v-btn>
                  <v-select
                    multiple
                    hide-details
                    item-value="id"
                    item-text="name"
                    label="Filter by tags"
                    :items="this.tags"
                    v-model="selectedTags"
                  />
                  <v-divider vertical class="ml-2 mr-2" />
                  <v-btn
                    outlined
                    color="primary"
                    :disabled="
                      !selectedTasks.length || activeTab === TabsStateMap.ALL
                    "
                    width="129"
                    @click="handleTaskActiveStateChange"
                  >
                    {{
                      activeTab === TabsStateMap.ACTIVE
                        ? 'Deactivate'
                        : 'Activate'
                    }}
                  </v-btn>
                </v-row>
              </v-col>
            </v-toolbar>
          </v-card-title>
        </v-card>
      </v-col>
      <v-progress-linear
        absolute
        bottom
        indeterminate
        rounded
        class="progress-bar"
        color="primary"
        height="3"
        :active="loading"
      />
    </v-row>
    <v-row dense>
      <v-col v-for="task in tasks" :key="task.id" xs="12" sm="6" md="4" lg="3">
        <TaskElement
          :task="task"
          :selectable="activeTab !== TabsStateMap.ALL"
          @generateLink="handleGenerateLink"
          @onReRun="reRun"
          @onCopyTask="copyTask"
          @onTaskActiveChanged="taskActiveChanged"
          @onOpenTaskComment="openTaskComment"
          @onDeepCloneTask="handleDeepCloneTask"
          @onTaskSelected="handleTaskSelected"
        />
      </v-col>
    </v-row>
    <div class="text-center mt-2">
      <v-pagination
        v-model="pagination.page"
        :length="pagination.pageCount"
        circle
      />
    </div>
    <GeneratedLinkDialog ref="generatedLinkDialog" />
    <TaskCommentDialog ref="taskCommentDialogRef" />
    <DeepCloneTaskDialog ref="deepCloneTaskDialogRef" />
    <TagsDialog ref="tagsDialogRef" @onTagAdded="handleNewTag" />
  </div>
</template>

<script>
import _ from 'lodash';
import TaskElement from '@/components/tasks/TaskElement';
import TaskCreationDialog from '@/components/tasks/TaskCreationDialog';
import GeneratedLinkDialog from '@/components/tasks/GeneratedLinkDialog';
import TaskCommentDialog from '@/components/tasks/TaskCommentDialog';
import DeepCloneTaskDialog from '@/components/tasks/DeepCloneTaskDialog';
import { DateTimeFormatter } from '@/shared/DateTime.formatter';
import {
  businessApiV1,
  processingResultsApiV1,
  tagsApiV1,
  tasksApiV1
} from '@/api/v1';
import {
  businessApiV2,
  processingResultsApiV2,
  tagsApiV2,
  tasksApiV2
} from '@/api/v2';
import TagsDialog from '@/components/dialogs/TagsDialog';
import CreateMatcherTaskV2 from '@/components/task-creation-v2/CreateMatcherTaskV2.vue';
import { DEFAULTS } from '@/constants';
import { generateCustomerLink } from '@/utils/generateCustomerLink';
import { MatcherTaskVersion } from '@/constants/task';

const SearchContextsMap = {
  BUSINESS_NAME: 'businessName',
  TASK_NAME: 'taskName'
};

const SearchContextOptions = [
  {
    text: 'Practice name',
    value: SearchContextsMap.BUSINESS_NAME
  },
  {
    text: 'Task name',
    value: SearchContextsMap.TASK_NAME
  }
];

const SearchContextOptionsMap = {
  [SearchContextsMap.BUSINESS_NAME]: SearchContextOptions[0],
  [SearchContextsMap.TASK_NAME]: SearchContextOptions[1]
};

const DefaultContextOption =
  SearchContextOptionsMap[SearchContextsMap.BUSINESS_NAME];

const TabsStateMap = {
  ACTIVE: ' active',
  DEACTIVATED: ' deactivated',
  ALL: ' all'
};

export default {
  data: () => ({
    TabsStateMap,
    dialog: false,
    tasksInterval: null,
    tasks: [],
    editedIndex: -1,
    pagination: {
      itemsPerPage: 12,
      page: 1,
      pageCount: 0
    },
    totalItems: 0,
    copiedTask: undefined,
    loading: false,
    activeTabIndex: 0,
    searchTerm: '',
    searchContext: DefaultContextOption,
    searchContexts: SearchContextOptions,
    businesses: [],
    searchSuggestions: [],
    tags: [],
    selectedTags: [],
    selectedTasks: []
  }),
  watch: {
    'pagination.page': function (newValue, oldValue) {
      if (newValue !== oldValue && oldValue) {
        this.getTasks();
      }
    },
    'pagination.itemsPerPage': function (newValue, oldValue) {
      if (newValue !== oldValue && oldValue) {
        this.getTasks();
      }
    },
    'pagination.sortBy': function (newValue, oldValue) {
      if (newValue !== oldValue && oldValue) {
        this.getTasks();
      }
    },
    'pagination.sortDesc': function (newValue, oldValue) {
      if (newValue !== oldValue && oldValue) {
        this.getTasks();
      }
    },
    searchContext: function () {
      this.setSearchSuggestions();

      if (this.searchTerm) {
        this.getTasks();
      }
    },
    dialog(val) {
      val || this.close();
    }
  },
  components: {
    TaskCreationDialog,
    TaskElement,
    GeneratedLinkDialog,
    TaskCommentDialog,
    DeepCloneTaskDialog,
    TagsDialog,
    CreateMatcherTaskV2
  },
  created() {
    this.initialize();
    this.getSavedBusinesses();
  },
  destroyed() {
    this.resetFetchTaskInterval();
  },
  computed: {
    activeTab() {
      if (this.activeTabIndex === 1) {
        return TabsStateMap.DEACTIVATED;
      }
      if (this.activeTabIndex === 2) {
        return TabsStateMap.ALL;
      }

      return TabsStateMap.ACTIVE;
    }
  },
  methods: {
    setSearchSuggestions() {
      const businessesNames = this.businesses.map(({ name }) => name);

      this.searchSuggestions =
        this.searchContext.value === DefaultContextOption.value
          ? businessesNames
          : [];
    },
    formatDateWithTime(date) {
      return DateTimeFormatter.formatWithTime(date);
    },
    getColor(status) {
      switch (status) {
        case 'completed':
          return 'success';
        case 'failed':
          return 'error';
        case 'in_progress':
          return 'primary';
      }
    },
    resetFetchTaskInterval() {
      if (this.tasksInterval) {
        clearInterval(this.tasksInterval);
        this.tasksInterval = null;
      }
    },
    startFetchTasksInterval() {
      this.resetFetchTaskInterval();
      this.tasksInterval = setInterval(() => {
        this.getTasks();
      }, 1500);
    },
    initialize() {
      this.loading = true;
      this.getTasks(true);
      this.getTags();
      this.startFetchTasksInterval();
    },
    getTasks(showLoader = false) {
      const searchParams = this.searchTerm
        ? {
            searchTerm: this.searchTerm,
            searchContext: this.searchContext.value
          }
        : {};

      (this.$store.getters.getFeatureToggleByNameEnabled('matcher_api_v2')
        ? tasksApiV2
        : tasksApiV1
      )
        .getAllTasks({
          page: this.pagination.page || 1,
          rowsPerPage: this.pagination.itemsPerPage,
          showActive:
            this.activeTab === TabsStateMap.ALL
              ? undefined
              : this.activeTab === TabsStateMap.ACTIVE,
          tags: this.selectedTags,
          ...searchParams
        })
        .then((res) => {
          this.tasks = res.items;
          this.totalItems = res.pagination.rowCount;
          this.pagination.pageCount = res.pagination.pageCount;

          if (showLoader) {
            this.loading = false;
          }
        })
        .catch((err) => {
          console.log('ERR: ', err);
          this.tasks = [];

          if (showLoader) {
            this.loading = false;
          }
        });
    },
    reRun(task_id) {
      return (
        this.$store.getters.getFeatureToggleByNameEnabled('matcher_api_v2')
          ? tasksApiV2
          : tasksApiV1
      )
        .reRunTask({
          taskId: task_id,
          action: 're-run'
        })
        .catch((err) => {
          console.log('ERR: ', err);
        });
    },
    openTaskCreationDialog() {
      if (this.$store.getters.isMatcherApiV2Enabled) {
        this.$refs.v2TaskCreationDialog.open();
      } else {
        this.$refs.creationDialog.open();
      }
    },
    async handleGenerateLink(result) {
      this.loading = true;
      const accessToken = await (
        this.$store.getters.isMatcherApiV2Enabled
          ? processingResultsApiV2
          : processingResultsApiV1
      ).fetchCustomerLinkAccessToken(
        result.id,
        DEFAULTS.LINKS_TYPES_MAP.CUSTOMER_LINK
      );

      const link = generateCustomerLink(
        result.id,
        DEFAULTS.LINKS_TYPES_MAP.CUSTOMER_LINK,
        accessToken,
        this.$store.getters.isMatcherApiV2Enabled
          ? MatcherTaskVersion.V2
          : MatcherTaskVersion.V1
      );

      this.$refs.generatedLinkDialog.open(link);
      this.loading = false;
    },
    copyTask(task) {
      if (this.$store.getters.isMatcherApiV2Enabled) {
        this.$refs.v2TaskCreationDialog.open(task);
      } else {
        this.$refs.creationDialog.open(task);
      }
    },
    async taskActiveChanged({ taskId, active }) {
      this.loading = true;
      await (
        this.$store.getters.getFeatureToggleByNameEnabled('matcher_api_v2')
          ? tasksApiV2
          : tasksApiV1
      ).updateTask(taskId, { active });
      this.loading = false;
    },
    openTaskComment({ taskId, comment, name }) {
      this.$refs.taskCommentDialogRef.open({ taskId, comment, name });
    },
    tabChange(tabNumber) {
      this.activeTabIndex = tabNumber;
      this.resetFetchTaskInterval();
      this.initialize(true);
      this.selectedTasks = [];
    },
    handleSearchChange: _.debounce(function (value) {
      if (this.searchTerm !== value) {
        this.searchTerm = value;
        this.loading = true;
        this.getTasks(true);
      }
    }, 300),
    getSavedBusinesses() {
      (this.$store.getters.isMatcherApiV2Enabled
        ? businessApiV2
        : businessApiV1
      )
        .fetchSavedBusinesses()
        .then((businesses) => {
          this.businesses = businesses;
          this.setSearchSuggestions();
        });
    },
    handleDeepCloneTask(task) {
      this.$refs.deepCloneTaskDialogRef.open(task);
    },
    async getTags() {
      this.tags = await (
        this.$store.getters.isMatcherApiV2Enabled ? tagsApiV2 : tagsApiV1
      ).fetchTags();
    },
    handleOpenTagsDialog() {
      this.$refs.tagsDialogRef.open();
    },
    handleNewTag(tag) {
      this.tags.push(tag);
    },
    async handleTaskActiveStateChange() {
      await (
        this.$store.getters.getFeatureToggleByNameEnabled('matcher_api_v2')
          ? tasksApiV2
          : tasksApiV1
      ).updateTasksBatch(this.selectedTasks, {
        active: this.activeTab !== TabsStateMap.ACTIVE
      });
      this.selectedTasks = [];
    },
    handleTaskSelected(taskId, selected) {
      if (selected) {
        this.selectedTasks.push(taskId);
      } else {
        this.selectedTasks = this.selectedTasks.filter((id) => id !== taskId);
      }
    }
  }
};
</script>

<style lang="scss">
.header {
  position: relative;

  .progress-bar {
    left: 4px;
    width: calc(100% - 8px);
  }

  .search-field {
    flex: 1;
    margin-right: 16px;
  }

  .search-context-select {
    max-width: 150px;
    margin-right: 16px;
  }
}
</style>
